// RraCallPage — the front-line call sheet (/rra-call/:token), RRA v2.1 (W3; F2,
// F3, G1, G4). It sits behind the staff sign-in (ProtectedRoute in App.tsx) and
// the API's caller roster; the page keeps its own Shell, no app chrome. The
// caller's name is the sign-in — there is no typed name field.
//
// It gives front line everything needed to phone a verifier and takes whatever
// happened:
//   1. header (who to call, role · phone · for whom),
//   2. "How to open the call" — the script past the front desk, the authority
//      sentence, the voicemail script, "Don't record the call.",
//   3. "What to confirm" — the spoken definition (when disability is in the gap)
//      and ONLY the questions the recorded gap opened,
//   4. "The signed request" — THIS verifier's copy on demand,
//   5. "Earlier calls" — caller, time, outcome (no need, no note),
//   6. the report form — five outcomes, each with its own fields.
// The ONLY free text is the optional call-logistics note (<=300). The server
// drops any out-of-gap answer, so this page cannot put one on the record even by
// accident. No ids are ever rendered.
//
// All sentences come from rraCopy.ts through the pure helpers in
// lib/rraCallClient.ts (which carry the logic and are unit-tested).

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import {
  CALL_NOTE_MAX,
  CALL_OUTCOME_CHOICES,
  CANT_HELP_REASONS,
  blankCallForm,
  buildCallBody,
  callErrorMessage,
  callFormProblem,
  callHeading,
  confirmLead,
  confirmQuestions,
  earlierCallLine,
  fetchRraCallCopy,
  getRraCall,
  isNotCallerError,
  noteHelper,
  noteLabel,
  openCallParagraphs,
  permissionCoversLine,
  showSpokenDefinition,
  sublineSegments,
  submitRraCall,
  telHref,
  type CallFormState,
  type RraCallView,
  type RraProngAnswer,
} from '@/lib/rraCallClient';
import {
  ANSWER_STAFF,
  CALL_OUTCOME,
  CALL_SHEET,
  SPOKEN_DEFINITION,
  cantHelpReasonLabel,
  disabilityQuestion,
  needQuestion,
  positionQuestion,
} from '@/lib/rraCopy';
import { OFFICE_PHONE } from '@/lib/officeContact';

const cardStyle = { padding: 'var(--space-4)' } as const;
const h2Style = { fontSize: 'var(--text-md)' } as const;

/** Yes · No · They couldn't say (the staff answer set), as a labelled radio group. */
function ProngRadios(props: {
  name: string;
  legend: string;
  value: RraProngAnswer | undefined;
  onChange: (v: RraProngAnswer) => void;
}) {
  const options: ReadonlyArray<{ value: RraProngAnswer; label: string }> = [
    { value: 'yes', label: ANSWER_STAFF.yes },
    { value: 'no', label: ANSWER_STAFF.no },
    { value: 'cant_say', label: ANSWER_STAFF.cantSay },
  ];
  return (
    <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }}>
      <legend className="label">{props.legend}</legend>
      <div className="cluster" style={{ gap: 'var(--space-5)', flexWrap: 'wrap' }}>
        {options.map((o) => (
          <label key={o.value} style={{ cursor: 'pointer' }}>
            <input
              type="radio"
              name={props.name}
              checked={props.value === o.value}
              onChange={() => props.onChange(o.value)}
            />{' '}
            {o.label}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export function RraCallPage() {
  const { token = '' } = useParams<{ token: string }>();
  const [view, setView] = useState<RraCallView | null>(null);
  const [notCaller, setNotCaller] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState<CallFormState | null>(null);

  // THE SIGNED REQUEST: this verifier's copy, fetched on demand with the sign-in
  // and shown from an object URL (a bare iframe src could not carry the Bearer token).
  const [copyUrl, setCopyUrl] = useState<string | null>(null);
  const [copyBusy, setCopyBusy] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const copyUrlRef = useRef<string | null>(null);

  const patch = (p: Partial<CallFormState>) =>
    setForm((f) => (f ? { ...f, ...p } : f));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const v = await getRraCall(token);
        if (cancelled) return;
        setView(v);
        setForm(blankCallForm(v));
      } catch (e) {
        if (cancelled) return;
        if (isNotCallerError(e)) setNotCaller(true);
        else setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Release the object URL when the page goes away.
  useEffect(
    () => () => {
      if (copyUrlRef.current) URL.revokeObjectURL(copyUrlRef.current);
    },
    [],
  );

  const showRequest = useCallback(async () => {
    setCopyBusy(true);
    setCopyFailed(false);
    try {
      const blob = await fetchRraCallCopy(token);
      const url = URL.createObjectURL(blob);
      if (copyUrlRef.current) URL.revokeObjectURL(copyUrlRef.current);
      copyUrlRef.current = url;
      setCopyUrl(url);
    } catch {
      setCopyFailed(true);
    } finally {
      setCopyBusy(false);
    }
  }, [token]);

  const submit = useCallback(async () => {
    if (!view || !form) return;
    const problem = callFormProblem(view, form);
    if (problem) {
      setError(problem);
      setSaved(false);
      return;
    }
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const res = await submitRraCall(token, buildCallBody(view, form));
      setView(res.view);
      if (res.ok) {
        setSaved(true);
        // Ready for the next report.
        setForm(blankCallForm(res.view));
      } else if (res.view.state === 'open') {
        // Not saved, yet the call is still open (a concurrent report won twice).
        // A closed or unavailable view re-renders as its own state instead.
        setError('The call could not be saved. Please try again.');
      }
    } catch (e) {
      if (isNotCallerError(e)) setNotCaller(true);
      else setError(callErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }, [view, form, token]);

  if (loading) return <Shell><p>Loading…</p></Shell>;

  if (notCaller)
    return (
      <Shell>
        <h1 className="card-title">Call sheet</h1>
        <p>{CALL_SHEET.notCaller}</p>
      </Shell>
    );

  if (!view || !form || view.state === 'unavailable')
    return (
      <Shell>
        <h1 className="card-title">This link is no longer available</h1>
        <p>If you believe this is an error, contact our office at {OFFICE_PHONE}.</p>
      </Shell>
    );

  if (view.state === 'closed')
    return (
      <Shell>
        <h1 className="card-title">This call is no longer needed</h1>
        <p>
          This verification has been answered or the request has closed. Thank you for following up.
        </p>
        {saved && <p style={{ color: 'var(--fg-muted)' }}>Saved. Thank you.</p>}
      </Shell>
    );

  const verifierName = view.verifier.name || 'the verifier';
  const subline = sublineSegments(view);
  const coversLine = permissionCoversLine(view);
  const questions = confirmQuestions(view);
  const showCopyButton = view.copyAvailable && !copyUrl && !copyFailed;

  return (
    <Shell>
      <p style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-sm)' }}>{CALL_SHEET.eyebrow}</p>
      <h1 className="card-title" style={{ marginBottom: 'var(--space-2)' }}>{callHeading(view)}</h1>
      <p style={{ color: 'var(--fg-muted)', marginTop: 0 }}>
        {subline.before}
        {subline.phone ? <a href={telHref(subline.phone)}>{subline.phone}</a> : null}
        {subline.after}
      </p>

      <div className="stack">
        <section className="card" style={cardStyle}>
          <h2 className="card-title" style={h2Style}>{CALL_SHEET.openHeading}</h2>
          <div className="stack" style={{ gap: 'var(--space-3)' }}>
            {openCallParagraphs(view).map((p) => (
              <p key={p} style={{ margin: 0 }}>{p}</p>
            ))}
          </div>
        </section>

        <section className="card" style={cardStyle}>
          <h2 className="card-title" style={h2Style}>{CALL_SHEET.confirmHeading}</h2>
          <p style={{ marginTop: 0 }}>{confirmLead(view)}</p>
          {showSpokenDefinition(view) && (
            <blockquote
              style={{
                margin: '0 0 var(--space-3)',
                paddingLeft: 'var(--space-4)',
                borderLeft: '3px solid var(--border)',
              }}
            >
              {SPOKEN_DEFINITION}
            </blockquote>
          )}
          <ol style={{ paddingLeft: 'var(--space-5)' }}>
            {questions.map((q) => (
              <li key={q.key}>
                {q.above?.map((line) => (
                  <div key={line} style={{ color: 'var(--fg-muted)' }}>{line}</div>
                ))}
                {q.text}
              </li>
            ))}
          </ol>
          {coversLine && <p>{coversLine}</p>}
          <div className="alert" data-tone="warning" role="note">
            <div className="alert-body">{CALL_SHEET.noMedical}</div>
          </div>
        </section>

        <section className="card" style={cardStyle}>
          <h2 className="card-title" style={h2Style}>{CALL_SHEET.requestHeading}</h2>
          {!view.copyAvailable || copyFailed ? (
            <p style={{ marginBottom: 0 }}>{CALL_SHEET.requestUnavailable}</p>
          ) : copyUrl ? (
            <>
              <iframe
                title={CALL_SHEET.requestHeading}
                src={copyUrl}
                style={{ width: '100%', height: 520, border: '1px solid var(--border)' }}
              />
              <p style={{ marginBottom: 0 }}>
                <a href={copyUrl} target="_blank" rel="noreferrer">
                  {CALL_SHEET.openInTab}
                </a>
              </p>
            </>
          ) : (
            showCopyButton && (
              <button
                className="btn"
                disabled={copyBusy}
                onClick={() => void showRequest()}
              >
                {copyBusy ? 'Loading…' : CALL_SHEET.showRequest}
              </button>
            )
          )}
        </section>

        {view.calls.length > 0 && (
          <section className="card" style={cardStyle}>
            <h2 className="card-title" style={h2Style}>{CALL_SHEET.earlierCalls}</h2>
            <ul style={{ margin: 0, paddingLeft: 'var(--space-5)' }}>
              {view.calls.map((c, i) => (
                <li key={`${c.at}-${i}`}>{earlierCallLine(c)}</li>
              ))}
            </ul>
          </section>
        )}

        <section className="card" style={cardStyle}>
          <h2 className="card-title" style={h2Style}>Report the call</h2>
          {saved && (
            <div className="alert" data-tone="success" role="status">
              <div className="alert-body">Saved. Thank you.</div>
            </div>
          )}
          {error && (
            <div className="alert" data-tone="danger" role="alert">
              <div className="alert-body">{error}</div>
            </div>
          )}

          <div className="stack">
            <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }}>
              <legend className="label">{CALL_OUTCOME.heading}</legend>
              <div className="stack" style={{ gap: 'var(--space-2)' }}>
                {CALL_OUTCOME_CHOICES.map((o) => (
                  // block: `.stack` only spaces its children, and these long choices must not run together
                  <label key={o.value} style={{ cursor: 'pointer', display: 'block' }}>
                    <input
                      type="radio"
                      name="rra-call-outcome"
                      checked={form.outcome === o.value}
                      onChange={() => patch({ outcome: o.value })}
                    />{' '}
                    {o.label}
                  </label>
                ))}
              </div>
            </fieldset>

            {form.outcome === 'call_back' && (
              <div className="field">
                <label className="label" htmlFor="rra-call-callback">{CALL_OUTCOME.callBackWhen}</label>
                <input
                  id="rra-call-callback"
                  className="input"
                  type="datetime-local"
                  required
                  value={form.callBackLocal}
                  onChange={(e) => patch({ callBackLocal: e.target.value })}
                />
              </div>
            )}

            {form.outcome === 'wants_writing' && (
              <div className="stack" style={{ gap: 'var(--space-3)' }}>
                <div className="field">
                  <label className="label" htmlFor="rra-call-writing-email">{CALL_OUTCOME.writingEmail}</label>
                  <input
                    id="rra-call-writing-email"
                    className="input"
                    type="email"
                    maxLength={320}
                    value={form.writingEmail}
                    onChange={(e) => patch({ writingEmail: e.target.value })}
                  />
                </div>
                <label style={{ cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={form.readBack}
                    onChange={(e) => patch({ readBack: e.target.checked })}
                  />{' '}
                  {CALL_OUTCOME.writingReadBack(verifierName)}
                </label>
              </div>
            )}

            {form.outcome === 'spoke' && (
              <div className="stack">
                <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }}>
                  <legend className="label">{positionQuestion(view.subjectName)}</legend>
                  <div className="cluster" style={{ gap: 'var(--space-5)' }}>
                    {(['yes', 'no'] as const).map((v) => (
                      <label key={v} style={{ cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="rra-call-position"
                          checked={form.positionToKnow === v}
                          onChange={() => patch({ positionToKnow: v })}
                        />{' '}
                        {ANSWER_STAFF[v]}
                      </label>
                    ))}
                  </div>
                </fieldset>
                {form.positionToKnow === 'yes' && view.askDisability && (
                  <ProngRadios
                    name="rra-call-disability"
                    legend={disabilityQuestion(view.subjectName)}
                    value={form.disability}
                    onChange={(v) => patch({ disability: v })}
                  />
                )}
                {form.positionToKnow === 'yes' && view.askNeed && (
                  <ProngRadios
                    name="rra-call-need"
                    legend={needQuestion(view.subjectName, view.needObject)}
                    value={form.need}
                    onChange={(v) => patch({ need: v })}
                  />
                )}
              </div>
            )}

            {form.outcome === 'cant_help_yet' && (
              <div className="stack" style={{ gap: 'var(--space-3)' }}>
                <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }}>
                  <legend className="label">{CALL_OUTCOME.why}</legend>
                  <div className="stack" style={{ gap: 'var(--space-2)' }}>
                    {CANT_HELP_REASONS.map((r) => (
                      <label key={r} style={{ cursor: 'pointer', display: 'block' }}>
                        <input
                          type="radio"
                          name="rra-call-reason"
                          checked={form.reason === r}
                          onChange={() => patch({ reason: r })}
                        />{' '}
                        {cantHelpReasonLabel(r, {
                          requesterName: view.requesterName,
                          signerName: view.signerName,
                        })}
                      </label>
                    ))}
                  </div>
                </fieldset>
                {form.reason === 'wrong_number' && (
                  <div className="field">
                    <label className="label" htmlFor="rra-call-better-phone">{CALL_OUTCOME.betterPhone}</label>
                    <input
                      id="rra-call-better-phone"
                      className="input"
                      type="tel"
                      maxLength={50}
                      value={form.betterPhone}
                      onChange={(e) => patch({ betterPhone: e.target.value })}
                    />
                  </div>
                )}
              </div>
            )}

            <div className="field">
              <label className="label" htmlFor="rra-call-note">{noteLabel(form)}</label>
              <textarea
                id="rra-call-note"
                className="input"
                rows={3}
                maxLength={CALL_NOTE_MAX}
                required={form.outcome === 'cant_help_yet' && form.reason === 'other'}
                value={form.note}
                onChange={(e) => patch({ note: e.target.value })}
              />
              <span style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-sm)' }}>
                {noteHelper(form.note)}
              </span>
            </div>

            <div className="field">
              <label className="label" htmlFor="rra-call-at">When the call happened</label>
              <input
                id="rra-call-at"
                className="input"
                type="datetime-local"
                value={form.atLocal}
                onChange={(e) => patch({ atLocal: e.target.value })}
              />
            </div>

            <button
              className="btn"
              data-variant="primary"
              disabled={form.outcome === null || busy}
              onClick={() => void submit()}
            >
              {busy ? 'Saving…' : 'Save the call'}
            </button>
          </div>
        </section>
      </div>
    </Shell>
  );
}

function Shell({ children }: { children: ReactNode }) {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', color: 'var(--fg)', fontFamily: 'var(--font-sans)', padding: 'var(--space-6)' }}>
      <div className="card" style={{ maxWidth: 680, margin: '0 auto', padding: 'var(--space-6)' }}>{children}</div>
    </div>
  );
}
